import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslationApp } from "../../../lib/i18next";
import ContentApprovalStatusChip from "../../../components/ContentApproval/ContentApprovalStatusChip";
import {
  CAROUSEL,
  CONTENT_APPROVAL_CREATIVE_CARD_TITLE,
  CONTENT_APPROVAL_CREATIVE_TYPE_LABEL,
  IMAGE,
  VIDEO,
} from "../../../i18n/keysTranslations";
import { BOX_SHADOW_FIELDS, INPUT_COLOR_BORDER } from "../../../utils/colors";
import { BORDER_RADIUS } from "../../../utils/constants";

const getMediaTypeLabel = (type, t) => {
  switch (type) {
    case "video":
      return t(VIDEO);
    case "carousel":
      return t(CAROUSEL);
    case "image":
    default:
      return t(IMAGE);
  }
};

const MOBILE_CARD_HEIGHT_SPACING = 9.5;
const MOBILE_VISIBLE_CARDS = 1.5;

const ContentApprovalCreativeCards = ({
  payloads = [],
  selectedIndex = 0,
  onSelect = () => {},
  getStatusForPayload = null,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const cardRefs = useRef([]);

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    cardRefs.current[selectedIndex]?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  }, [isMobile, selectedIndex]);

  if (payloads.length <= 1) {
    return null;
  }

  const mobileCardHeight = theme.spacing(MOBILE_CARD_HEIGHT_SPACING);
  const mobileCardsGap = theme.spacing(1.5);
  const mobileScrollMaxHeight = `calc(${mobileCardHeight} * ${MOBILE_VISIBLE_CARDS} + ${mobileCardsGap})`;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        flexWrap: { xs: "nowrap", sm: "wrap" },
        gap: 1.5,
        mb: 2,
        flexShrink: 0,
        overflowY: { xs: "auto", sm: "visible" },
        maxHeight: { xs: mobileScrollMaxHeight, sm: "none" },
        WebkitOverflowScrolling: "touch",
        pr: { xs: 0.5, sm: 0 },
      }}
    >
      {payloads.map((payload, index) => {
        const isSelected = index === selectedIndex;
        const typeLabel = getMediaTypeLabel(payload?.type, t);
        const cardStatus = getStatusForPayload
          ? getStatusForPayload({ payload })
          : payload?.status;

        return (
          <Box
            key={payload?.contentID || index}
            ref={(element) => {
              cardRefs.current[index] = element;
            }}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(index)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(index);
              }
            }}
            sx={{
              flex: { xs: "0 0 auto", sm: "1 1 calc(50% - 12px)" },
              width: { xs: "100%", sm: "auto" },
              minHeight: { xs: mobileCardHeight, sm: "auto" },
              minWidth: { sm: 200 },
              maxWidth: { md: 280 },
              bgcolor: "background.paper",
              borderRadius: BORDER_RADIUS,
              border: `2px solid ${isSelected ? "#111" : INPUT_COLOR_BORDER}`,
              boxShadow: isSelected ? BOX_SHADOW_FIELDS : "none",
              p: 1.5,
              cursor: "pointer",
              transition: "border-color 0.15s ease, box-shadow 0.15s ease",
              "&:hover": {
                borderColor: isSelected ? "#111" : "text.secondary",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {t(CONTENT_APPROVAL_CREATIVE_CARD_TITLE, { number: index + 1 })}
              </Typography>
              <ContentApprovalStatusChip status={cardStatus} />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t(CONTENT_APPROVAL_CREATIVE_TYPE_LABEL, { type: typeLabel })}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default ContentApprovalCreativeCards;
